import { useEffect, useState } from "react";
import api from "../../lib/api";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";
import { Eye, Users, Repeat, Clock, Globe, MapPin, Building2, FileText } from "lucide-react";

const PERIODS = [
    { key: "day", label: "Daily" },
    { key: "week", label: "Weekly" },
    { key: "month", label: "Monthly" },
];

function fmtTime(sec) {
    if (!sec) return "0s";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m ? `${m}m ${s}s` : `${s}s`;
}

const Card = ({ icon: Icon, label, value, sub, testid }) => (
    <div data-testid={testid} className="rounded-2xl border border-blue-400/15 bg-blue-500/5 p-5">
        <div className="flex items-center gap-2 text-blue-200/60">
            <Icon className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
        </div>
        <p className="mt-2 font-serif text-3xl text-blue-50">{value}</p>
        {sub && <p className="mt-1 text-xs text-blue-200/45">{sub}</p>}
    </div>
);

const GeoList = ({ icon: Icon, title, items, testid }) => (
    <div data-testid={testid} className="rounded-2xl border border-blue-400/15 bg-blue-500/5 p-5">
        <div className="mb-3 flex items-center gap-2 text-blue-100">
            <Icon className="h-4 w-4 text-sky-300" />
            <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        {items.length === 0 ? (
            <p className="text-xs text-blue-200/40">No data yet</p>
        ) : (
            <ul className="space-y-2">
                {items.map((it) => (
                    <li key={it.name} className="flex items-center justify-between text-sm">
                        <span className="truncate text-blue-100/80">{it.name}</span>
                        <span className="ml-3 shrink-0 rounded-full bg-blue-500/15 px-2 py-0.5 text-xs text-sky-200">
                            {it.count}
                        </span>
                    </li>
                ))}
            </ul>
        )}
    </div>
);

export default function Analytics() {
    const [period, setPeriod] = useState("day");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/analytics?period=${period}`)
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, [period]);

    const s = data?.summary;
    const t = s?.time;
    const at = data?.all_time;
    const periodLabel = { day: "today", week: "this week", month: "this month" }[period];

    return (
        <div data-testid="analytics-page" className="space-y-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="font-serif text-2xl text-blue-50">Site Analytics</h1>
                    <p className="mt-1 text-sm text-blue-200/50">
                        Private to the team · visitor activity {periodLabel}
                    </p>
                </div>
                <div className="inline-flex rounded-full border border-blue-400/20 bg-blue-500/5 p-1">
                    {PERIODS.map((p) => (
                        <button
                            key={p.key}
                            data-testid={`analytics-period-${p.key}`}
                            onClick={() => setPeriod(p.key)}
                            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                                period === p.key
                                    ? "bg-sky-400 text-[#0b1220]"
                                    : "text-blue-200/60 hover:text-blue-100"
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading || !data ? (
                <p className="text-sm text-blue-200/50">Loading…</p>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                        <Card testid="stat-pageviews" icon={Eye} label={`Page views (${periodLabel})`} value={s.pageviews} sub={`${at.pageviews} all-time`} />
                        <Card testid="stat-unique" icon={Users} label="Unique visitors" value={s.unique} sub={`${at.unique} all-time`} />
                        <Card testid="stat-returning" icon={Repeat} label="Returning" value={s.returning} sub={`${at.returning} all-time`} />
                        <Card testid="stat-time" icon={Clock} label="Avg time on site" value={fmtTime(t.avg_sec)} sub={`New ${fmtTime(t.new_avg_sec)} · Return ${fmtTime(t.return_avg_sec)}`} />
                    </div>

                    <div data-testid="analytics-chart" className="rounded-2xl border border-blue-400/15 bg-blue-500/5 p-5">
                        <h3 className="mb-4 text-sm font-semibold text-blue-100">
                            Page views &amp; unique visitors ({PERIODS.find((p) => p.key === period).label.toLowerCase()})
                        </h3>
                        {data.series.length === 0 ? (
                            <p className="py-10 text-center text-sm text-blue-200/40">
                                No visits recorded yet — data appears here as people browse the site.
                            </p>
                        ) : (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={data.series} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" vertical={false} />
                                    <XAxis dataKey="label" tick={{ fill: "#9bb4d4", fontSize: 11 }} tickLine={false} axisLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fill: "#9bb4d4", fontSize: 11 }} tickLine={false} axisLine={false} />
                                    <Tooltip
                                        contentStyle={{ background: "#0f1b2d", border: "1px solid #22314d", borderRadius: 12, color: "#e6eefc" }}
                                        cursor={{ fill: "#ffffff08" }}
                                    />
                                    <Bar dataKey="pageviews" name="Page views" fill="#38bdf8" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="unique" name="Unique" fill="#818cf8" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    <div className="grid gap-4 lg:grid-cols-3">
                        <GeoList testid="geo-countries" icon={Globe} title="Top countries" items={data.geo.countries} />
                        <GeoList testid="geo-regions" icon={MapPin} title="Top regions / states" items={data.geo.regions} />
                        <GeoList testid="geo-cities" icon={Building2} title="Top cities" items={data.geo.cities} />
                    </div>

                    <GeoList testid="top-pages" icon={FileText} title="Most-visited pages" items={data.top_pages.map((p) => ({ name: p.name, count: p.count }))} />
                </>
            )}
        </div>
    );
}
